// A UI element the user may see inside a child app; the path is relative to
// the app ("/dispatch" for /apps/amthermal/dispatch).
export interface UiElement {
  path: string;
  privilege?: string;
}

export interface ChildApp {
  slug: string;
  label: string;
  /** A short-lived signed link to the app's mount.js on the artifact blob container. */
  entry: string;
  apiBaseUrl: string;
  description?: string;
  version?: string;
  privilege?: string;
  expiresAt?: string;
  uiElements: UiElement[];
}

export type AppListErrorKind = "unauthorized" | "forbidden" | "unavailable" | "failed";

export class AppListError extends Error {
  readonly kind: AppListErrorKind;

  constructor(kind: AppListErrorKind, message: string) {
    super(message);
    this.name = "AppListError";
    this.kind = kind;
  }
}

const isApiUrl = (value: unknown) =>
  typeof value === "string" &&
  (value.startsWith("https://") ||
    value.startsWith("http://localhost:") ||
    value.startsWith("http://127.0.0.1:"));

const isEntryUrl = (value: unknown) =>
  typeof value === "string" &&
  (value.startsWith("/") ||
    value.startsWith("https://") ||
    value.startsWith("http://localhost:") ||
    value.startsWith("http://127.0.0.1:"));

const isOptionalString = (value: unknown) => value === undefined || typeof value === "string";

// Lenient on purpose: a missing or badly shaped list becomes [], and bad
// entries are dropped, so uiElements can never make the app list fail.
function parseUiElements(value: unknown): UiElement[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry: unknown) => {
    const element = entry as { path?: unknown; privilege?: unknown } | null;
    if (typeof element?.path !== "string" || !element.path.startsWith("/")) return [];
    if (element.privilege !== undefined && typeof element.privilege !== "string") return [];
    return [
      typeof element.privilege === "string"
        ? { path: element.path, privilege: element.privilege }
        : { path: element.path },
    ];
  });
}

const isChildApp = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== "object") return false;
  const app = value as Record<string, unknown>;
  return (
    typeof app.slug === "string" &&
    /^[a-z0-9][a-z0-9-]*$/.test(app.slug) &&
    typeof app.label === "string" &&
    app.label.length > 0 &&
    isEntryUrl(app.entry) &&
    isApiUrl(app.apiBaseUrl) &&
    isOptionalString(app.description) &&
    isOptionalString(app.version) &&
    isOptionalString(app.privilege) &&
    isOptionalString(app.expiresAt)
  );
};

// The entitlements payload ({ apps: [...] }). Anything else, including the
// plain UI_ELEMENT array AMP's own tabs read, means "no child apps".
export function parseAppList(data: unknown): ChildApp[] {
  const apps = (data as { apps?: unknown } | null)?.apps;
  if (!Array.isArray(apps)) return [];
  const seen = new Set<string>();
  return apps.flatMap((app: unknown) => {
    if (!isChildApp(app) || seen.has(app.slug as string)) return [];
    seen.add(app.slug as string);
    return [
      {
        slug: app.slug as string,
        label: app.label as string,
        entry: app.entry as string,
        apiBaseUrl: app.apiBaseUrl as string,
        description: app.description as string | undefined,
        version: app.version as string | undefined,
        privilege: app.privilege as string | undefined,
        expiresAt: app.expiresAt as string | undefined,
        uiElements: parseUiElements(app.uiElements),
      },
    ];
  });
}

const statusError = (status: number) => {
  if (status === 401) return new AppListError("unauthorized", "Your sign-in was rejected.");
  if (status === 403)
    return new AppListError("forbidden", "Your account doesn't have access to these apps.");
  if (status === 503) return new AppListError("unavailable", "Apps are temporarily unavailable.");
  return new AppListError("failed", `App list request failed (${status})`);
};

// Never cached: entry links expire after a few minutes, so every page load
// and every import retry needs a fresh list. REACT_APP_CHILD_APPS_PRODUCT sets
// the Product header that selects this audience's list at the entitlements
// service; it falls back to AMP's existing REACT_APP_PRODUCT_APIM.
export async function loadChildApps(): Promise<ChildApp[]> {
  const apiBaseUrl = process.env.REACT_APP_ENTITLEMENTS_API;
  if (!apiBaseUrl) throw new AppListError("failed", "REACT_APP_ENTITLEMENTS_API is not set.");

  // Imported here so parseAppList stays free of the MSAL/axios chain (and testable in Jest).
  const { getHeaders } = await import("../components/ApiHeaders/apiHeadersEntitlements");
  const headers: Record<string, string> = { ...(await getHeaders()) };
  const product = process.env.REACT_APP_CHILD_APPS_PRODUCT;
  if (product) headers.Product = product;

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/entitlements-mapping/user?po_type_code=UI_ELEMENT`, {
      cache: "no-store",
      headers,
    });
  } catch {
    throw new AppListError("unavailable", "Apps could not be reached.");
  }
  if (!response.ok) throw statusError(response.status);

  // The envelope every BETM service wraps its payload in: { meta, request, response: { data } }.
  const body = (await response.json()) as { response?: { data?: unknown } };
  return applyDevEntryOverrides(parseAppList(body?.response?.data));
}

// Local dev only: REACT_APP_DEV_ENTRY_OVERRIDES maps app slugs to a local
// mount.js, e.g. amthermal=/__local/amthermal-ui/mount.js (served by
// src/setupProxy.js). NODE_ENV is "production" in a built bundle, so this can
// never change a deployed AMP's app list.
export const parseOverrides = (raw: string | undefined) => {
  const overrides = new Map<string, string>();
  if (!raw) return overrides;
  for (const pair of raw.split(",")) {
    const [slug, entry] = pair.split("=").map((part) => part.trim());
    if (slug && entry) overrides.set(slug, entry);
  }
  return overrides;
};

// Pure part of the dev override: point each overridden app at its local entry,
// and add an app the list did not grant (the entitlements side may not exist
// yet) with a placeholder label and the API base URL given for local runs.
export function applyOverrides(
  apps: ChildApp[],
  overrides: Map<string, string>,
  bust: string,
  devApiBaseUrl: string,
  forceApiBaseUrl = false,
): ChildApp[] {
  if (overrides.size === 0) return apps;
  const withBust = (entry: string) => `${entry}${entry.includes("?") ? "&" : "?"}${bust}`;
  const known = new Set(apps.map((app) => app.slug));
  const overridden = apps.map((app) => {
    const entry = overrides.get(app.slug);
    if (!entry) return app;
    // A granted app keeps the list's apiBaseUrl unless the developer asked for a local one.
    return { ...app, entry: withBust(entry), ...(forceApiBaseUrl ? { apiBaseUrl: devApiBaseUrl } : {}) };
  });
  const added = [...overrides.entries()]
    .filter(([slug]) => !known.has(slug))
    .map(([slug, entry]): ChildApp => ({
      slug,
      label: slug,
      entry: withBust(entry),
      apiBaseUrl: devApiBaseUrl,
      uiElements: [],
    }));
  return [...overridden, ...added];
}

export function applyDevEntryOverrides(apps: ChildApp[]): ChildApp[] {
  if (process.env.NODE_ENV !== "development") return apps;
  const overrides = parseOverrides(process.env.REACT_APP_DEV_ENTRY_OVERRIDES);
  if (overrides.size === 0) return apps;
  console.info(`[amp] using local child entries: ${[...overrides.values()].join(", ")}`);
  // A per-page-load query string, so a rebuilt mount.js is picked up on the
  // next reload instead of the browser reusing the previous module.
  return applyOverrides(
    apps,
    overrides,
    `v=${Date.now()}`,
    process.env.REACT_APP_DEV_CHILD_API_URL || "http://localhost:8000",
    Boolean(process.env.REACT_APP_DEV_CHILD_API_URL),
  );
}
